import { CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { TranslatePipe } from '@ngx-translate/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, debounceTime, finalize, takeUntil } from 'rxjs';
import { SelectModule } from 'primeng/select';
import { WorkflowDataStoreService } from '../services/workflowDataStore.service';
import {
  WorkflowDataStore,
  WorkflowDataStoreListFilter,
  WorkflowDataStorePutIfAbsentResult,
  WorkflowDataStorePutResult,
  WorkflowDataStoreRecord,
  WorkflowDataStoreRecordStatus,
  WorkflowDataStoreRemoveResult,
} from './workflow-data-store';

type WorkflowDataStoreRow = WorkflowDataStoreRecord & {
  editing: boolean;
  saving: boolean;
  deleting: boolean;
  valueText: string;
  expectedValueText: string;
  errorKey?: string;
  deleteErrorKey?: string;
};

type NewWorkflowDataStoreRow = {
  key: string;
  valueText: string;
  saving: boolean;
  errorKey?: string;
};

@Component({
  selector: 'app-workflow-data-store-state',
  templateUrl: './workflow-data-store-state.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ErrorComponent,
    TranslatePipe,
    DatePipe,
    SelectModule,
  ],
})
export class WorkflowDataStoreStateComponent implements OnInit, OnDestroy {
  modalRef = inject(DynamicDialogRef);
  private readonly dialogConfig = inject(
    DynamicDialogConfig<{
      workflowDataStore: WorkflowDataStore;
      canEdit: boolean;
    }>,
  );
  private workflowDataStoreService = inject(WorkflowDataStoreService);

  workflowDataStore: WorkflowDataStore = {} as WorkflowDataStore;

  canEdit = false;

  prefix = '';

  status?: WorkflowDataStoreRecordStatus;

  readonly statusOptions: WorkflowDataStoreRecordStatus[] = [
    'OPEN',
    'CLAIMED',
    'COMPLETE',
    'RELEASED',
  ];

  rows: WorkflowDataStoreRow[] = [];

  newRow?: NewWorkflowDataStoreRow;

  loadingRecords = false;

  recordsError?: HttpErrorResponse;

  private prefixChangeEvent = new Subject<string>();
  private destroyEvent = new Subject<void>();

  ngOnInit(): void {
    Object.assign(this, this.dialogConfig.data);
    this.prefixChangeEvent
      .pipe(debounceTime(500), takeUntil(this.destroyEvent))
      .subscribe(() => this.loadRecords());
    this.loadRecords();
  }

  ngOnDestroy(): void {
    this.destroyEvent.next();
    this.destroyEvent.complete();
  }

  hide(): void {
    this.modalRef.close();
  }

  loadRecords(): void {
    if (!this.workflowDataStore?.identifier) {
      return;
    }
    this.loadingRecords = true;
    this.recordsError = undefined;
    this.workflowDataStoreService
      .listRecords(this.workflowDataStore.identifier, this.buildFilter())
      .pipe(
        finalize(() => {
          this.loadingRecords = false;
        }),
      )
      .subscribe({
        next: (result) => {
          this.rows = result.records.map((record) => this.toRow(record));
        },
        error: (error: HttpErrorResponse) => {
          this.recordsError = error;
          this.rows = [];
        },
      });
  }

  onPrefixChange(value: string): void {
    this.prefix = value;
    this.prefixChangeEvent.next(value);
  }

  onStatusChange(
    value: WorkflowDataStoreRecordStatus | null | undefined,
  ): void {
    this.status = value ?? undefined;
    this.loadRecords();
  }

  startAddRow(): void {
    this.newRow = {
      key: '',
      valueText: '{}',
      saving: false,
    };
  }

  cancelAddRow(): void {
    this.newRow = undefined;
  }

  saveNewRow(): void {
    if (!this.newRow) {
      return;
    }
    const key = this.newRow.key.trim();

    if (key === '') {
      this.newRow.errorKey = 'error.required';
      return;
    }

    const parsedValue = this.parseJson(this.newRow.valueText);
    if (!parsedValue.ok) {
      this.newRow.errorKey = 'workflow_data_store.state.errors.invalid-json';
      return;
    }

    this.newRow.saving = true;
    this.newRow.errorKey = undefined;

    this.workflowDataStoreService
      .putRecordIfAbsent(
        this.workflowDataStore.identifier,
        key,
        parsedValue.value,
      )
      .pipe(
        finalize(() => {
          if (this.newRow) {
            this.newRow.saving = false;
          }
        }),
      )
      .subscribe({
        next: (result) => {
          if (!result.created) {
            if (this.newRow) {
              this.newRow.errorKey = this.getReasonKey(result.reason);
            }
            return;
          }
          this.afterMutation(result);
        },
        error: (error: HttpErrorResponse) => {
          if (this.newRow) {
            this.newRow.errorKey = this.getRequestErrorKey(error);
          }
        },
      });
  }

  startEdit(row: WorkflowDataStoreRow): void {
    row.editing = true;
    row.errorKey = undefined;
    row.valueText = this.stringifyValue(row.value);
    row.expectedValueText = '';
  }

  cancelEdit(row: WorkflowDataStoreRow): void {
    row.editing = false;
    row.errorKey = undefined;
    row.valueText = this.stringifyValue(row.value);
    row.expectedValueText = '';
  }

  saveRow(row: WorkflowDataStoreRow): void {
    const parsedValue = this.parseJson(row.valueText);
    if (!parsedValue.ok) {
      row.errorKey = 'workflow_data_store.state.errors.invalid-json';
      return;
    }

    const expectedValueText = row.expectedValueText.trim();
    let expectedValue: unknown | undefined;
    if (expectedValueText !== '') {
      const parsedExpectedValue = this.parseJson(expectedValueText);
      if (!parsedExpectedValue.ok) {
        row.errorKey = 'workflow_data_store.state.errors.invalid-json';
        return;
      }
      expectedValue = parsedExpectedValue.value;
    }

    row.saving = true;
    row.errorKey = undefined;

    this.workflowDataStoreService
      .putRecord(
        this.workflowDataStore.identifier,
        row.key,
        parsedValue.value,
        expectedValue,
      )
      .pipe(
        finalize(() => {
          row.saving = false;
        }),
      )
      .subscribe({
        next: (result) => {
          if (!result.ok) {
            row.errorKey = this.getReasonKey(result.reason);
            return;
          }
          this.afterMutation(result);
        },
        error: (error: HttpErrorResponse) => {
          row.errorKey = this.getRequestErrorKey(error);
        },
      });
  }

  deleteRow(row: WorkflowDataStoreRow): void {
    row.deleting = true;
    row.deleteErrorKey = undefined;

    this.workflowDataStoreService
      .removeRecord(this.workflowDataStore.identifier, row.key)
      .pipe(
        finalize(() => {
          row.deleting = false;
        }),
      )
      .subscribe({
        next: (result) => {
          if (!result.ok) {
            row.deleteErrorKey = this.getReasonKey(result.reason);
            return;
          }
          this.loadRecords();
        },
        error: (error: HttpErrorResponse) => {
          row.deleteErrorKey = this.getRequestErrorKey(error);
        },
      });
  }

  private afterMutation(
    result:
      | WorkflowDataStorePutResult
      | WorkflowDataStorePutIfAbsentResult
      | WorkflowDataStoreRemoveResult,
  ): void {
    this.newRow = undefined;
    this.loadRecords();
    if ('record' in result && result.record != null) {
      this.rows = this.rows.map((row) =>
        row.key === result.record?.key ? this.toRow(result.record) : row,
      );
    }
  }

  private buildFilter(): WorkflowDataStoreListFilter {
    const filter: WorkflowDataStoreListFilter = {};
    const prefix = this.prefix.trim();
    if (prefix !== '') {
      filter.prefix = prefix;
    }
    if (this.status) {
      filter.status = this.status;
    }
    return filter;
  }

  private toRow(record: WorkflowDataStoreRecord): WorkflowDataStoreRow {
    return {
      ...record,
      editing: false,
      saving: false,
      deleting: false,
      valueText: this.stringifyValue(record.value),
      expectedValueText: '',
    };
  }

  private stringifyValue(value: unknown): string {
    if (value === undefined) {
      return '';
    }
    return JSON.stringify(value, null, 2);
  }

  private parseJson(
    value: string,
  ): { ok: true; value: unknown } | { ok: false } {
    try {
      return { ok: true, value: JSON.parse(value) };
    } catch {
      return { ok: false };
    }
  }

  private getReasonKey(reason: string | null | undefined): string {
    if (!reason) {
      return 'workflow_data_store.state.errors.generic';
    }
    return `workflow_data_store.state.errors.${reason}`;
  }

  private getRequestErrorKey(error: HttpErrorResponse): string {
    if (typeof error.error === 'string') {
      try {
        const parsed = JSON.parse(error.error) as { reason?: string };
        return this.getReasonKey(parsed.reason);
      } catch {
        return 'workflow_data_store.state.errors.generic';
      }
    }
    if (error.error && typeof error.error === 'object') {
      return this.getReasonKey((error.error as { reason?: string }).reason);
    }
    return 'workflow_data_store.state.errors.generic';
  }
}
